'use client';

import type React from 'react';
import Link from 'next/link';
import { motion } from 'framer-motion';
import * as LucideIcons from 'lucide-react';
import SectionBase from '@/components/ui/sections/section-base';
import SectionHeader from '@/components/ui/sections/section-header';
import { TypographyH4, TypographySmall } from '@/components/ui/typography';
import { cls } from '@/lib/utils';

const SECTION_ID = 'service-area-services';

const wrapperMotionProps = {
  initial: 'hidden' as const,
  whileInView: 'visible' as const,
  viewport: { once: true },
  variants: {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.2 },
    },
  },
};

const cardMotionProps = {
  variants: {
    hidden: { opacity: 0, y: 30 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.5 } },
  },
};

function getIcon(iconName: string) {
  const icons = LucideIcons as unknown as Record<
    string,
    React.ComponentType<{ className?: string }>
  >;
  const Icon = icons[iconName];
  return Icon || LucideIcons.Home;
}

interface ServicesSectionProperties {
  services: {
    icon: string;
    title: string;
    description: string;
    href: string;
  }[];
  townName: string;
}

function ServicesSection({ services, townName }: ServicesSectionProperties) {
  return (
    <SectionBase id={SECTION_ID} background="bg-card">
      <motion.div
        className={cls`flex flex-col items-center gap-10`}
        {...wrapperMotionProps}
      >
        <SectionHeader
          subtitle="OUR SERVICES"
          title={`What We Build in ${townName}`}
          description={`Cornerstone Homes offers a full range of custom construction and renovation services for ${townName} homeowners.`}
        />

        <div
          className={cls`grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 w-full`}
        >
          {services.map((service) => {
            const Icon = getIcon(service.icon);
            return (
              <motion.div key={service.title} {...cardMotionProps}>
                <Link
                  href={service.href}
                  className="group flex flex-col gap-3 p-6 rounded-lg bg-background hover:bg-primary/5 border border-border transition-colors h-full"
                >
                  <Icon className="text-primary size-8" />
                  <TypographyH4 className="text-foreground group-hover:text-primary transition-colors">
                    {service.title}
                  </TypographyH4>
                  <TypographySmall className="text-muted-foreground leading-relaxed">
                    {service.description}
                  </TypographySmall>
                </Link>
              </motion.div>
            );
          })}
        </div>
      </motion.div>
    </SectionBase>
  );
}

export default ServicesSection;
